import * as Sentry from '@sentry/react';

/**
 * GradMap — Sentry (frontend / gradmap-frontend).
 * Error monitoring only (no performance tracing).
 * DSN from REACT_APP_SENTRY_DSN; fallback is the public ingest key.
 */

const DSN =
  process.env.REACT_APP_SENTRY_DSN ||
  'https://14592d389899332821fc66db8d25ab2d@o4511457424637952.ingest.us.sentry.io/4511928550424576';

export function initSentry(): void {
  if (!DSN) return;

  Sentry.init({
    dsn: DSN,
    // REACT_APP_ENV is already staging | production on server .env.
    environment:
      process.env.REACT_APP_SENTRY_ENVIRONMENT ||
      process.env.REACT_APP_ENV ||
      process.env.NODE_ENV ||
      'production',
    tracesSampleRate: 0,
    ignoreErrors: [
      'SESSION_EXPIRED',
      'SESSION_EXISTS',
      'Session revoked',
      'Non-Error promise rejection captured',
    ],
    beforeSend(event, hint) {
      const err: any = hint?.originalException;
      const msg = (err && (err.message || String(err))) || '';
      if (/Network Error|timeout of|canceled|cancelled|ECONNABORTED/i.test(msg)) {
        return null;
      }
      return event;
    },
  });
}

export function reportUnexpected(error: unknown, context?: Record<string, any>): void {
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

/** Strip ids so 200 students hitting the same missing endpoint = one Sentry issue. */
export function fingerprintApiPath(url?: string): string {
  if (!url) return 'unknown';
  return url
    .split('?')[0]
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id')
    .replace(/\/\d+/g, '/:id');
}

/**
 * Missing-data / 404 that we caused (lost college link, no row when the page needs one).
 * Warning-level so it is visible and grouped, without looking like a crash.
 */
export function reportMissingData(context: {
  url?: string;
  method?: string;
  status?: number;
  responseMessage?: unknown;
}): void {
  const method = (context.method || 'GET').toUpperCase();
  const path = fingerprintApiPath(context.url);
  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setTag('issue_type', 'missing-data');
    scope.setFingerprint(['missing-data', method, path]);
    scope.setExtra('url', context.url);
    scope.setExtra('method', method);
    scope.setExtra('status', context.status);
    scope.setExtra('responseMessage', context.responseMessage);
    Sentry.captureMessage(`Missing data: ${method} ${path}`);
  });
}

/** Attach signed-in user id only (no email — FERPA). Call with no args to clear. */
export function setSentryUser(userId?: string | number): void {
  if (!userId) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: String(userId) });
}

export { Sentry };
